<template>
  <div id="keap-details-drawer">
    <integration-details-drawer-wrapper
      title="Keap"
      :logo="InfusionKeap"
      sub-heading="Integration Added"
      :store-data="storeData"
      :show-status="!isLoading"
      :fields-title="'Keap Fields Mapping to Maropost Fields'"
    >
      <template #integrationInfo>
        <skelton-loader v-if="isLoading" :is-loading="isLoading" />
        <div id="keap-details-wrapper" v-else>
          <v-row class="py-1">
            <v-col cols="3" class="py-0 text--secondary subtitle-2">
              Account Domain
            </v-col>
            <v-col class="py-0 body-2">{{ integration.name }} </v-col>
          </v-row>
          <v-row class="py-1">
            <v-col cols="3" class="py-0 text--secondary subtitle-2">
              Shop Name
            </v-col>
            <v-col class="py-0 body-2">{{ integration.nick_name }} </v-col>
          </v-row>
          <v-row class="py-1">
            <v-col cols="3" class="py-0 text--secondary subtitle-2">
              Maropost List
            </v-col>
            <v-col class="py-0 body-2"> {{ listName() }} </v-col>
          </v-row>
          <v-row class="py-1">
            <v-col cols="3" class="py-0 text--secondary subtitle-2">
              Integration Type
            </v-col>
            <v-col class="py-0 body-2">
              {{ integration.integration_type | underscoreRemover }}
            </v-col>
          </v-row>
        </div>
      </template>
      <template #dataFields>
        <integration-mapping-fields
          :fields="mappingDataFields"
          id="keap-fields-table"
          sub-title="Maropost Fields"
          field-heading="Keap Fields"
        />
        <br />
      </template>
    </integration-details-drawer-wrapper>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { $integrations } from "@/services";
import isEmpty from "lodash/isEmpty";

import InfusionKeap from "@/assets/conectiontab/infusion-keap.png";
import IntegrationDetailsDrawerWrapper from "@/components/shared/IntegrationDetailsDrawerWrapper.vue";
import IntegrationMappingFields from "@/components/shared/IntegrationMappingFields.vue";

import SkeltonLoader from "@/components/shared/SkeltonLoader.vue";

const { getStoresList } = $integrations;

/**
 * Keap integration details drawer
 */
export default {
  name: "KeapDetailDrawer",
  /**
  |--------------------------------------------------
  | Components
  |--------------------------------------------------
  */
  components: {
    SkeltonLoader,
    IntegrationMappingFields,
    IntegrationDetailsDrawerWrapper
  },
  /**
  |--------------------------------------------------
  | Data properties
  |--------------------------------------------------
  */
  data() {
    return {
      isLoading: false,
      InfusionKeap,
      storeData: { active: false },
      /**
       * Data fields mapping for product_contact_import Keap integration
       */
      fields: [
        { field: "For orders:", text: "" },
        { field: "grand_total", text: "total" },
        { field: "order_date", text: "order_date" },
        { field: "original_order_id", text: "id" },
        { field: "order_status", text: "status" },
        { field: "For each order item:", text: "" },
        { field: "description", text: "description" },
        { field: "item_id", text: "id" },
        { field: "price", text: "price" },
        { field: "quantity", text: "quantity" },
        {
          field:
            "Contact records come from Keap as a hash of email address, first name, and last name:",
          text: ""
        },
        { field: "email", text: "email" },
        { field: "first_name", text: "first_name" },
        { field: "last_name", text: "last_name" }
      ],
      /**
       * Data fields mapping for contact_tags Keap integration
       */
      contactImportFields: [
        { field: "For Tags:", text: "" },
        { field: "name", text: "name" },
        { field: "Contact records come from Keap as a hash:", text: "" },
        { field: "email", text: "email" },
        { field: "first_name", text: "first_name" },
        { field: "last_name", text: "last_name" },
        { field: "phone", text: "phone" },
        { field: "fax", text: "fax" }
      ],
      integration: {}
    };
  },
  /**
  |--------------------------------------------------
  | Computed properties
  |--------------------------------------------------
  */
  computed: {
    ...mapGetters({
      drawer: "ui/drawer"
    }),
    /**
     * New Integration detail
     */
    newIntegration() {
      const { integration } = this.drawer.right;
      return integration;
    },
    /**
     * Hides data fields table
     */
    hideDataFields() {
      const { newIntegration } = this;
      return !["tags_import"].includes(newIntegration.integrationType);
    },
    mappingDataFields() {
      if (this.newIntegration.integrationType === "contacts_import") {
        return this.contactImportFields;
      }
      return this.fields;
    }
  },
  /**
  |--------------------------------------------------
  | Methods
  |--------------------------------------------------
  */
  methods: {
    /**
     * Fetches list of integrations for keap
     */
    async getKeapConnections() {
      try {
        if (isEmpty(this.newIntegration)) return;

        this.isLoading = true;
        let { data } = await getStoresList({ crm_type: "keap" });

        const index = data.findIndex(this.isIntegrationValid);

        this.integration = !isEmpty(data[index]) ? data[index] : {};
        this.storeData.active = !!data[index];
        if (this.newIntegration.integrationType === "contacts_import" && this.integration.data) {
          this.contactImportFields.push({field: "Custom Fields", text: ""})
          Object.keys(this.integration.data).forEach(key => {
            this.contactImportFields.push({field: key, text: this.integration.data[key]})
          });
        }
      } catch (error) {
        this.$store.commit("ui/snackbar", {
          message: error.message,
          type: "error"
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Verifies that integration is valid or not
     * @returns {Boolean}
     */
    isIntegrationValid({ integration_type, crm_lists }) {
      const isIntegrationSame =
        integration_type === this.newIntegration.integrationType;


      return (
        isIntegrationSame &&
        crm_lists.findIndex(
          ({ list_id }) => this.newIntegration.list_id === list_id
        ) > -1
      );
    },
    /**
     * Name of the list whose intehration have been added
     */
    listName() {
      if (isEmpty(this.integration.crm_lists)) return "";
      return this.integration.crm_lists[0].list.name;
    }
  },
  /**
  |--------------------------------------------------
  | Created lifecycle hook
  |--------------------------------------------------
  */
  created() {
    this.getKeapConnections();
  }
};
</script>
<style lang="scss" scoped>
.td-bg--grey {
  background-color: #f2f2f2;
}
#keap-fields-table {
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  border-radius: 4px;
}
</style>
